import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '../i18n/messages';
import {
  POST_ROUTE_INDEX,
  getLocalizedPostRouteSlug,
  getPostRouteEntry,
  resolveLegacyPostRouteEntryByLocalizedSlug,
  resolvePostRouteEntryByLocalizedSlug,
} from '../blog/postRouteIndex';
import {
  IMAGE_TOOLS,
  IMAGE_TOOLS_LASTMOD,
  getImageToolByLocalizedSlug,
  getImageToolBySlug,
  getImageToolLocalizedSlug,
  getImageToolsRouteSegment,
} from '../imageToolsData';

export type LandingSectionKey =
  | 'features'
  | 'sizes'
  | 'how'
  | 'apps'
  | 'privacy'
  | 'faq';

export type SitePageKey =
  | 'home'
  | LandingSectionKey
  | 'contact'
  | 'about'
  | 'editorialPolicy'
  | 'privacyPolicy'
  | 'termsOfService'
  | 'iosApp'
  | 'passportPhotoMaker'
  | 'passportPhotoChecker'
  | 'digitalPassportPhoto'
  | 'twoByTwoPhotoMaker'
  | 'usPassportPhotoMaker'
  | 'passport600x600Maker'
  | 'usVisaPhotoMaker'
  | 'passportPhotoCropTool'
  | 'passportPhotoResizer'
  | 'passportRequirements'
  | 'visaRequirements'
  | 'idRequirements'
  | 'drivingLicenceRequirements'
  | 'biometricRequirements'
  | 'studentIdRequirements'
  | 'workPermitRequirements'
  | 'noSignup'
  | 'noWatermark'
  | 'withoutUploading'
  | 'sheetTemplate'
  | 'driversLicenseMaker'
  | 'greenCard2x2'
  | 'ukPassport600x750'
  | 'mmPixelsCalculator'
  | 'dpiPixels'
  | 'printLayouts'
  | 'idImposer'
  | 'backgroundRemoval'
  | 'backgroundRemovalService'
  | 'sizeComparison'
  | 'headSizeRules'
  | 'blog'
  | 'guide'
  | 'app';

export type RouteMatch =
  | { locale: Locale; page: 'landing'; section?: LandingSectionKey }
  | { locale: Locale; page: 'contact' }
  | { locale: Locale; page: 'about' }
  | { locale: Locale; page: 'editorial-policy' }
  | { locale: Locale; page: 'privacy-policy' }
  | { locale: Locale; page: 'terms-of-service' }
  | { locale: Locale; page: 'ios-app' }
  | { locale: Locale; page: 'passport-photo-maker' }
  | { locale: Locale; page: 'passport-photo-checker' }
  | { locale: Locale; page: 'digital-passport-photo' }
  | { locale: Locale; page: '2x2-passport-photo-maker' }
  | { locale: Locale; page: 'us-passport-photo-maker' }
  | { locale: Locale; page: '600x600-passport-photo-maker' }
  | { locale: Locale; page: 'us-visa-photo-maker' }
  | { locale: Locale; page: 'passport-photo-crop-tool' }
  | { locale: Locale; page: 'passport-photo-resizer' }
  | { locale: Locale; page: 'passport-requirements' }
  | { locale: Locale; page: 'visa-requirements' }
  | { locale: Locale; page: 'id-requirements' }
  | { locale: Locale; page: 'driving-licence-requirements' }
  | { locale: Locale; page: 'biometric-requirements' }
  | { locale: Locale; page: 'student-id-requirements' }
  | { locale: Locale; page: 'work-permit-requirements' }
  | { locale: Locale; page: 'no-signup' }
  | { locale: Locale; page: 'no-watermark' }
  | { locale: Locale; page: 'without-uploading' }
  | { locale: Locale; page: 'sheet-template' }
  | { locale: Locale; page: 'drivers-license-maker' }
  | { locale: Locale; page: 'green-card-2x2' }
  | { locale: Locale; page: 'uk-passport-600x750' }
  | { locale: Locale; page: 'mm-pixels-calculator' }
  | { locale: Locale; page: 'dpi-pixels' }
  | { locale: Locale; page: 'print-layouts' }
  | { locale: Locale; page: 'id-imposer' }
  | { locale: Locale; page: 'background-removal' }
  | { locale: Locale; page: 'background-removal-service' }
  | { locale: Locale; page: 'background-removal-pricing' }
  | { locale: Locale; page: 'background-removal-contact' }
  | { locale: Locale; page: 'background-removal-dashboard' }
  | { locale: Locale; page: 'image-tools' }
  | { locale: Locale; page: 'image-tool'; toolSlug: string }
  | { locale: Locale; page: 'size-comparison' }
  | { locale: Locale; page: 'head-size-rules' }
  | { locale: Locale; page: 'blog-list' }
  | { locale: Locale; page: 'blog-post'; baseSlug?: string; routeSlug: string }
  | { locale: Locale; page: 'guide' }
  | { locale: Locale; page: 'app' };

const LOCALE_PREFIXES: Record<Locale, string> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  ar: 'ar',
  'zh-Hans': 'zh',
  de: 'de',
  pt: 'pt',
};

const PAGE_SEGMENTS: Record<Locale, Record<Exclude<SitePageKey, 'home'>, string>> = {
  en: {
    features: 'features',
    sizes: 'sizes',
    how: 'how',
    apps: 'apps',
    privacy: 'privacy',
    faq: 'faq',
    contact: 'contact',
    about: 'about',
    editorialPolicy: 'editorial-policy',
    privacyPolicy: 'privacy-policy',
    termsOfService: 'terms-of-service',
    iosApp: 'ios-app',
    passportPhotoMaker: 'passport-photo-maker',
    passportPhotoChecker: 'passport-photo-checker',
    digitalPassportPhoto: 'digital-passport-photo',
    twoByTwoPhotoMaker: '2x2-passport-photo-maker',
    usPassportPhotoMaker: 'us-passport-photo-maker',
    passport600x600Maker: '600x600-passport-photo-maker',
    usVisaPhotoMaker: 'us-visa-photo-maker',
    passportPhotoCropTool: 'passport-photo-crop-tool',
    passportPhotoResizer: 'passport-photo-resizer',
    passportRequirements: 'passport-photo-requirements',
    visaRequirements: 'visa-photo-requirements',
    idRequirements: 'id-photo-requirements',
    drivingLicenceRequirements: 'driving-licence-photo-requirements',
    biometricRequirements: 'biometric-photo-requirements',
    studentIdRequirements: 'student-id-photo-requirements',
    workPermitRequirements: 'work-permit-photo-requirements',
    noSignup: 'passport-photo-maker-no-signup',
    noWatermark: 'passport-photo-maker-no-watermark',
    withoutUploading: 'passport-photo-maker-without-uploading',
    sheetTemplate: 'passport-photo-sheet-template',
    driversLicenseMaker: 'drivers-license-photo-maker',
    greenCard2x2: 'green-card-photo-requirements-2x2',
    ukPassport600x750: 'uk-passport-photo-600x750',
    mmPixelsCalculator: 'passport-photo-mm-to-pixels-calculator',
    dpiPixels: 'passport-photo-dpi-and-pixels',
    printLayouts: 'passport-photo-print-layouts',
    idImposer: 'id-imposer',
    backgroundRemoval: 'passport-photo-background-removal',
    backgroundRemovalService: 'background-removal',
    sizeComparison: 'passport-photo-size-comparison',
    headSizeRules: 'passport-photo-head-size-rules',
    blog: 'blog',
    guide: 'guide',
    app: 'app',
  },
  es: {
    features: 'caracteristicas',
    sizes: 'tamanos',
    how: 'como-funciona',
    apps: 'aplicaciones',
    privacy: 'privacidad',
    faq: 'preguntas-frecuentes',
    contact: 'contacto',
    about: 'acerca-de',
    editorialPolicy: 'politica-editorial',
    privacyPolicy: 'politica-de-privacidad',
    termsOfService: 'terminos-del-servicio',
    iosApp: 'ios-app',
    passportPhotoMaker: 'creador-de-fotos-para-pasaporte',
    passportPhotoChecker: 'verificador-de-fotos-para-pasaporte',
    digitalPassportPhoto: 'foto-digital-para-pasaporte',
    twoByTwoPhotoMaker: 'creador-de-foto-2x2-para-pasaporte',
    usPassportPhotoMaker: 'creador-de-foto-para-pasaporte-de-ee-uu',
    passport600x600Maker: 'creador-de-foto-de-pasaporte-600x600',
    usVisaPhotoMaker: 'creador-de-foto-para-visa-de-ee-uu',
    passportPhotoCropTool: 'herramienta-para-recortar-foto-de-pasaporte',
    passportPhotoResizer: 'redimensionador-de-foto-de-pasaporte',
    passportRequirements: 'requisitos-de-foto-para-pasaporte',
    visaRequirements: 'requisitos-de-foto-para-visa',
    idRequirements: 'requisitos-de-foto-para-documento-de-identidad',
    drivingLicenceRequirements: 'requisitos-de-foto-para-permiso-de-conducir',
    biometricRequirements: 'requisitos-de-foto-biometrica',
    studentIdRequirements: 'requisitos-de-foto-para-carnet-estudiantil',
    workPermitRequirements: 'requisitos-de-foto-para-permiso-de-trabajo',
    noSignup: 'creador-de-fotos-para-pasaporte-sin-registro',
    noWatermark: 'creador-de-fotos-para-pasaporte-sin-marca-de-agua',
    withoutUploading: 'creador-de-fotos-para-pasaporte-sin-subir',
    sheetTemplate: 'plantilla-de-hoja-para-foto-de-pasaporte',
    driversLicenseMaker: 'creador-de-fotos-para-licencia-de-conducir',
    greenCard2x2: 'requisitos-de-foto-para-green-card-2x2',
    ukPassport600x750: 'foto-para-pasaporte-del-reino-unido-600x750',
    mmPixelsCalculator: 'calculadora-de-mm-a-pixeles-para-foto-de-pasaporte',
    dpiPixels: 'pixeles-y-dpi-de-foto-para-pasaporte',
    printLayouts: 'disenos-de-impresion-de-foto-para-pasaporte',
    idImposer: 'impositor-de-id',
    backgroundRemoval: 'eliminacion-de-fondo-para-foto-de-pasaporte',
    backgroundRemovalService: 'eliminar-fondo',
    sizeComparison: 'comparacion-de-tamanos-de-foto-para-pasaporte',
    headSizeRules: 'reglas-del-tamano-de-la-cabeza-en-fotos-de-pasaporte',
    blog: 'blog',
    guide: 'guia',
    app: 'app',
  },
  fr: {
    features: 'fonctionnalites',
    sizes: 'formats',
    how: 'comment-ca-marche',
    apps: 'applications',
    privacy: 'confidentialite',
    faq: 'faq',
    contact: 'contact',
    about: 'a-propos',
    editorialPolicy: 'politique-editoriale',
    privacyPolicy: 'politique-de-confidentialite',
    termsOfService: 'conditions-d-utilisation',
    iosApp: 'ios-app',
    passportPhotoMaker: 'createur-photo-passeport',
    passportPhotoChecker: 'verificateur-photo-passeport',
    digitalPassportPhoto: 'photo-passeport-numerique',
    twoByTwoPhotoMaker: 'createur-photo-passeport-2x2',
    usPassportPhotoMaker: 'createur-photo-passeport-etats-unis',
    passport600x600Maker: 'createur-photo-passeport-600x600',
    usVisaPhotoMaker: 'createur-photo-visa-etats-unis',
    passportPhotoCropTool: 'outil-recadrage-photo-passeport',
    passportPhotoResizer: 'redimensionneur-photo-passeport',
    passportRequirements: 'exigences-photo-passeport',
    visaRequirements: 'exigences-photo-visa',
    idRequirements: 'exigences-photo-carte-identite',
    drivingLicenceRequirements: 'exigences-photo-permis-de-conduire',
    biometricRequirements: 'exigences-photo-biometrique',
    studentIdRequirements: 'exigences-photo-carte-etudiante',
    workPermitRequirements: 'exigences-photo-permis-de-travail',
    noSignup: 'createur-de-photo-passeport-sans-inscription',
    noWatermark: 'createur-de-photo-passeport-sans-filigrane',
    withoutUploading: 'createur-de-photo-passeport-sans-televersement',
    sheetTemplate: 'modele-de-planche-photo-passeport',
    driversLicenseMaker: 'createur-de-photo-pour-permis-de-conduire',
    greenCard2x2: 'exigences-photo-carte-verte-2x2',
    ukPassport600x750: 'photo-passeport-royaume-uni-600x750',
    mmPixelsCalculator: 'calculateur-mm-vers-pixels-photo-passeport',
    dpiPixels: 'pixels-et-dpi-photo-passeport',
    printLayouts: 'mises-en-page-d-impression-photo-passeport',
    idImposer: 'imposeur-carte-identite',
    backgroundRemoval: 'suppression-du-fond-photo-passeport',
    backgroundRemovalService: 'supprimer-le-fond',
    sizeComparison: 'comparaison-des-tailles-photo-passeport',
    headSizeRules: 'regles-de-taille-de-tete-photo-passeport',
    blog: 'blog',
    guide: 'guide',
    app: 'app',
  },
  ar: {
    features: 'الميزات',
    sizes: 'المقاسات',
    how: 'كيف-يعمل',
    apps: 'التطبيقات',
    privacy: 'الخصوصية',
    faq: 'الاسئلة-الشائعة',
    contact: 'اتصل-بنا',
    about: 'حول',
    editorialPolicy: 'السياسة-التحريرية',
    privacyPolicy: 'سياسة-الخصوصية',
    termsOfService: 'شروط-الخدمة',
    iosApp: 'ios-app',
    passportPhotoMaker: 'صانع-صور-جواز-السفر',
    passportPhotoChecker: 'مدقق-صور-جواز-السفر',
    digitalPassportPhoto: 'صورة-جواز-سفر-رقمية',
    twoByTwoPhotoMaker: 'صانع-صورة-جواز-سفر-2x2',
    usPassportPhotoMaker: 'صانع-صورة-جواز-سفر-امريكي',
    passport600x600Maker: 'صانع-صورة-جواز-سفر-600x600',
    usVisaPhotoMaker: 'صانع-صورة-تاشيرة-امريكية',
    passportPhotoCropTool: 'اداة-قص-صورة-جواز-السفر',
    passportPhotoResizer: 'تغيير-مقاس-صورة-جواز-السفر',
    passportRequirements: 'متطلبات-صور-جواز-السفر',
    visaRequirements: 'متطلبات-صور-التاشيرة',
    idRequirements: 'متطلبات-صور-بطاقة-الهوية',
    drivingLicenceRequirements: 'متطلبات-صور-رخصة-القيادة',
    biometricRequirements: 'متطلبات-الصور-البيومترية',
    studentIdRequirements: 'متطلبات-صور-بطاقة-الطالب',
    workPermitRequirements: 'متطلبات-صور-تصريح-العمل',
    noSignup: 'صانع-صور-جواز-السفر-بدون-تسجيل',
    noWatermark: 'صانع-صور-جواز-السفر-بدون-علامة-مائية',
    withoutUploading: 'صانع-صور-جواز-السفر-بدون-رفع',
    sheetTemplate: 'قالب-ورقة-صور-جواز-السفر',
    driversLicenseMaker: 'صانع-صور-رخصة-القيادة',
    greenCard2x2: 'متطلبات-صورة-البطاقة-الخضراء-2x2',
    ukPassport600x750: 'صورة-جواز-سفر-بريطانية-600x750',
    mmPixelsCalculator: 'حاسبة-تحويل-مم-الى-بكسل-لصور-جواز-السفر',
    dpiPixels: 'بكسل-ودي-بي-آي-صور-جواز-السفر',
    printLayouts: 'تخطيطات-طباعة-صور-جواز-السفر',
    idImposer: 'ترتيب-بطاقة-الهوية',
    backgroundRemoval: 'ازالة-خلفية-صور-جواز-السفر',
    backgroundRemovalService: 'ازالة-الخلفية',
    sizeComparison: 'مقارنة-مقاسات-صور-جواز-السفر',
    headSizeRules: 'قواعد-حجم-الرأس-في-صور-جواز-السفر',
    blog: 'المدونة',
    guide: 'الدليل',
    app: 'app',
  },
  'zh-Hans': {
    features: '功能',
    sizes: '尺寸',
    how: '如何使用',
    apps: '应用',
    privacy: '隐私',
    faq: '常见问题',
    contact: '联系',
    about: '关于',
    editorialPolicy: '编辑政策',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    iosApp: 'ios-app',
    passportPhotoMaker: '护照照片制作器',
    passportPhotoChecker: '护照照片检查器',
    digitalPassportPhoto: '数字护照照片',
    twoByTwoPhotoMaker: '2x2护照照片制作器',
    usPassportPhotoMaker: '美国护照照片制作器',
    passport600x600Maker: '600x600护照照片制作器',
    usVisaPhotoMaker: '美国签证照片制作器',
    passportPhotoCropTool: '护照照片裁剪工具',
    passportPhotoResizer: '护照照片调整大小',
    passportRequirements: '护照照片要求',
    visaRequirements: '签证照片要求',
    idRequirements: '身份证照片要求',
    drivingLicenceRequirements: '驾照照片要求',
    biometricRequirements: '生物识别照片要求',
    studentIdRequirements: '学生证照片要求',
    workPermitRequirements: '工作许可照片要求',
    noSignup: '护照照片制作器-无需注册',
    noWatermark: '护照照片制作器-无水印',
    withoutUploading: '护照照片制作器-无需上传',
    sheetTemplate: '护照照片排版模板',
    driversLicenseMaker: '驾照照片制作器',
    greenCard2x2: '绿卡照片要求-2x2',
    ukPassport600x750: '英国护照照片-600x750',
    mmPixelsCalculator: '护照照片毫米转像素计算器',
    dpiPixels: '护照照片像素和dpi',
    printLayouts: '护照照片打印版式',
    idImposer: '身份证排版工具',
    backgroundRemoval: '护照照片背景移除',
    backgroundRemovalService: '移除背景',
    sizeComparison: '护照照片尺寸对比',
    headSizeRules: '护照照片头部尺寸规则',
    blog: '博客',
    guide: '指南',
    app: 'app',
  },
  de: {
    features: 'funktionen',
    sizes: 'formate',
    how: 'so-funktioniert-es',
    apps: 'apps',
    privacy: 'datenschutz',
    faq: 'faq',
    contact: 'kontakt',
    about: 'uber-uns',
    editorialPolicy: 'redaktionsrichtlinie',
    privacyPolicy: 'datenschutzerklarung',
    termsOfService: 'nutzungsbedingungen',
    iosApp: 'ios-app',
    passportPhotoMaker: 'passfoto-maker',
    passportPhotoChecker: 'passfoto-pruefer',
    digitalPassportPhoto: 'digitales-passfoto',
    twoByTwoPhotoMaker: '2x2-passfoto-maker',
    usPassportPhotoMaker: 'us-passfoto-maker',
    passport600x600Maker: '600x600-passfoto-maker',
    usVisaPhotoMaker: 'us-visafoto-maker',
    passportPhotoCropTool: 'passfoto-zuschneiden-tool',
    passportPhotoResizer: 'passfoto-resizer',
    passportRequirements: 'passfoto-anforderungen',
    visaRequirements: 'visafoto-anforderungen',
    idRequirements: 'ausweisfoto-anforderungen',
    drivingLicenceRequirements: 'fuhrerscheinfoto-anforderungen',
    biometricRequirements: 'biometrische-foto-anforderungen',
    studentIdRequirements: 'studentenausweisfoto-anforderungen',
    workPermitRequirements: 'arbeitserlaubnisfoto-anforderungen',
    noSignup: 'passfoto-maker-ohne-anmeldung',
    noWatermark: 'passfoto-maker-ohne-wasserzeichen',
    withoutUploading: 'passfoto-maker-ohne-hochladen',
    sheetTemplate: 'passfoto-bogen-vorlage',
    driversLicenseMaker: 'fuhrerscheinfoto-maker',
    greenCard2x2: 'green-card-fotoanforderungen-2x2',
    ukPassport600x750: 'uk-passfoto-600x750',
    mmPixelsCalculator: 'passfoto-mm-zu-pixel-rechner',
    dpiPixels: 'passfoto-pixel-und-dpi',
    printLayouts: 'passfoto-drucklayouts',
    idImposer: 'ausweis-imposer',
    backgroundRemoval: 'passfoto-hintergrund-entfernen',
    backgroundRemovalService: 'hintergrund-entfernen',
    sizeComparison: 'passfoto-groessenvergleich',
    headSizeRules: 'passfoto-kopfgroessen-regeln',
    blog: 'blog',
    guide: 'anleitung',
    app: 'app',
  },
  pt: {
    features: 'recursos',
    sizes: 'tamanhos',
    how: 'como-funciona',
    apps: 'aplicativos',
    privacy: 'privacidade',
    faq: 'faq',
    contact: 'contato',
    about: 'sobre',
    editorialPolicy: 'politica-editorial',
    privacyPolicy: 'politica-de-privacidade',
    termsOfService: 'termos-de-servico',
    iosApp: 'ios-app',
    passportPhotoMaker: 'criador-de-foto-para-passaporte',
    passportPhotoChecker: 'verificador-de-foto-para-passaporte',
    digitalPassportPhoto: 'foto-digital-para-passaporte',
    twoByTwoPhotoMaker: 'criador-de-foto-2x2-para-passaporte',
    usPassportPhotoMaker: 'criador-de-foto-para-passaporte-dos-eua',
    passport600x600Maker: 'criador-de-foto-de-passaporte-600x600',
    usVisaPhotoMaker: 'criador-de-foto-para-visto-dos-eua',
    passportPhotoCropTool: 'ferramenta-de-recorte-de-foto-para-passaporte',
    passportPhotoResizer: 'redimensionador-de-foto-para-passaporte',
    passportRequirements: 'requisitos-de-foto-para-passaporte',
    visaRequirements: 'requisitos-de-foto-para-visto',
    idRequirements: 'requisitos-de-foto-para-documento-de-identidade',
    drivingLicenceRequirements: 'requisitos-de-foto-para-carteira-de-motorista',
    biometricRequirements: 'requisitos-de-foto-biometrica',
    studentIdRequirements: 'requisitos-de-foto-para-carteirinha-estudantil',
    workPermitRequirements: 'requisitos-de-foto-para-permissao-de-trabalho',
    noSignup: 'criador-de-foto-para-passaporte-sem-cadastro',
    noWatermark: 'criador-de-foto-para-passaporte-sem-marca-dagua',
    withoutUploading: 'criador-de-foto-para-passaporte-sem-upload',
    sheetTemplate: 'modelo-de-folha-para-foto-de-passaporte',
    driversLicenseMaker: 'criador-de-foto-para-carteira-de-motorista',
    greenCard2x2: 'requisitos-de-foto-para-green-card-2x2',
    ukPassport600x750: 'foto-de-passaporte-do-reino-unido-600x750',
    mmPixelsCalculator: 'calculadora-de-mm-para-pixels-de-foto-para-passaporte',
    dpiPixels: 'pixels-e-dpi-de-foto-para-passaporte',
    printLayouts: 'layouts-de-impressao-de-foto-para-passaporte',
    idImposer: 'impositor-de-id',
    backgroundRemoval: 'remocao-de-fundo-para-foto-de-passaporte',
    backgroundRemovalService: 'remover-fundo',
    sizeComparison: 'comparacao-de-tamanhos-de-foto-para-passaporte',
    headSizeRules: 'regras-de-tamanho-da-cabeca-em-fotos-de-passaporte',
    blog: 'blog',
    guide: 'guia',
    app: 'app',
  },
};

const LANDING_SECTION_KEYS: LandingSectionKey[] = ['features', 'sizes', 'how', 'apps', 'privacy', 'faq'];

const STATIC_ROUTE_LASTMOD: Record<Exclude<SitePageKey, 'app'>, string> = {
  home: '2026-06-06',
  features: '2026-05-20',
  sizes: '2026-05-20',
  how: '2026-05-20',
  apps: '2026-05-26',
  privacy: '2026-05-20',
  faq: '2026-05-20',
  contact: '2026-04-04',
  about: '2026-04-01',
  editorialPolicy: '2026-05-15',
  privacyPolicy: '2026-08-11',
  termsOfService: '2026-08-11',
  iosApp: '2026-05-28',
  passportPhotoMaker: '2026-06-03',
  passportPhotoChecker: '2026-06-03',
  digitalPassportPhoto: '2026-05-14',
  twoByTwoPhotoMaker: '2026-06-03',
  usPassportPhotoMaker: '2026-05-24',
  passport600x600Maker: '2026-05-24',
  usVisaPhotoMaker: '2026-05-26',
  passportPhotoCropTool: '2026-05-26',
  passportPhotoResizer: '2026-06-03',
  passportRequirements: '2026-05-26',
  visaRequirements: '2026-04-05',
  idRequirements: '2026-05-26',
  drivingLicenceRequirements: '2026-04-05',
  biometricRequirements: '2026-04-05',
  studentIdRequirements: '2026-04-05',
  workPermitRequirements: '2026-04-05',
  noSignup: '2026-05-26',
  noWatermark: '2026-04-05',
  withoutUploading: '2026-05-26',
  sheetTemplate: '2026-04-05',
  driversLicenseMaker: '2026-04-05',
  greenCard2x2: '2026-04-05',
  ukPassport600x750: '2026-04-05',
  mmPixelsCalculator: '2026-04-05',
  dpiPixels: '2026-04-05',
  printLayouts: '2026-05-26',
  idImposer: '2026-05-22',
  backgroundRemoval: '2026-05-26',
  backgroundRemovalService: '2026-05-20',
  sizeComparison: '2026-04-05',
  headSizeRules: '2026-04-05',
  blog: '2026-04-02',
  guide: '2026-05-26',
};
const APP_ROUTE_LASTMOD = '2026-06-07';
const BACKGROUND_REMOVAL_CONTACT_LASTMOD = '2026-05-10';
const BACKGROUND_REMOVAL_PRICING_LASTMOD = '2026-05-09';
const STANDALONE_SITEMAP_ENTRIES: Array<{ pathname: string; lastmod: string }> = [
  { pathname: '/id-imposer-app/', lastmod: '2026-05-22' },
  { pathname: '/llm.txt', lastmod: '2026-05-26' },
  { pathname: '/llms.txt', lastmod: '2026-07-06' },
  { pathname: '/llms-full.txt', lastmod: '2026-07-06' },
];

const NON_HOME_KEYS: Exclude<SitePageKey, 'home'>[] = [
  'features',
  'sizes',
  'how',
  'apps',
  'privacy',
  'faq',
  'contact',
  'about',
  'editorialPolicy',
  'privacyPolicy',
  'termsOfService',
  'iosApp',
  'passportPhotoMaker',
  'passportPhotoChecker',
  'digitalPassportPhoto',
  'twoByTwoPhotoMaker',
  'usPassportPhotoMaker',
  'passport600x600Maker',
  'usVisaPhotoMaker',
  'passportPhotoCropTool',
  'passportPhotoResizer',
  'passportRequirements',
  'visaRequirements',
  'idRequirements',
  'drivingLicenceRequirements',
  'biometricRequirements',
  'studentIdRequirements',
  'workPermitRequirements',
  'noSignup',
  'noWatermark',
  'withoutUploading',
  'sheetTemplate',
  'driversLicenseMaker',
  'greenCard2x2',
  'ukPassport600x750',
  'mmPixelsCalculator',
  'dpiPixels',
  'printLayouts',
  'idImposer',
  'backgroundRemoval',
  'backgroundRemovalService',
  'sizeComparison',
  'headSizeRules',
  'blog',
  'guide',
  'app',
];

const prefixToLocale = new Map<string, Locale>(
  (Object.entries(LOCALE_PREFIXES) as Array<[Locale, string]>).map(([locale, prefix]) => [prefix, locale]),
);

const localeSegmentIndex = new Map<Locale, Map<string, Exclude<SitePageKey, 'home'>>>();
for (const locale of SUPPORTED_LOCALES) {
  const index = new Map<string, Exclude<SitePageKey, 'home'>>();
  for (const key of NON_HOME_KEYS) {
    index.set(PAGE_SEGMENTS[locale][key], key);
  }
  localeSegmentIndex.set(locale, index);
}

function decodePathSegments(pathname: string): string[] {
  return pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
}

export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/index.html') return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

export function getPathLocale(pathname: string): Locale {
  const segments = decodePathSegments(normalizePathname(pathname));
  const prefixedLocale = segments[0] ? prefixToLocale.get(segments[0]) : undefined;
  if (prefixedLocale) return prefixedLocale;
  return DEFAULT_LOCALE;
}

function lookupSegment(locale: Locale, segment: string): Exclude<SitePageKey, 'home'> | undefined {
  return localeSegmentIndex.get(locale)?.get(segment)
    ?? localeSegmentIndex.get('en')?.get(segment);
}

export function matchPathname(pathname: string): RouteMatch {
  const normalized = normalizePathname(pathname);
  const segments = decodePathSegments(normalized);

  let locale: Locale = DEFAULT_LOCALE;
  if (segments[0]) {
    const prefixedLocale = prefixToLocale.get(segments[0]);
    if (prefixedLocale) {
      locale = prefixedLocale;
      segments.shift();
    }
  }

  if (segments.length === 0) return { locale, page: 'landing' };

  const [first, second] = segments;
  const imageToolsSegment = getImageToolsRouteSegment(locale);
  if (first === imageToolsSegment || first === getImageToolsRouteSegment(DEFAULT_LOCALE)) {
    if (segments.length === 1) {
      return { locale, page: 'image-tools' };
    }
    const tool = getImageToolByLocalizedSlug(locale, second) ?? getImageToolBySlug(second);
    if (segments.length === 2 && tool) {
      return { locale, page: 'image-tool', toolSlug: tool.id };
    }
  }

  const firstKey = lookupSegment(locale, first);

  if (segments.length === 2 && firstKey === 'backgroundRemovalService' && second === 'contact') {
    return { locale, page: 'background-removal-contact' };
  }

  if (segments.length === 2 && firstKey === 'backgroundRemovalService' && second === 'pricing') {
    return { locale, page: 'background-removal-pricing' };
  }

  if (segments.length === 2 && firstKey === 'backgroundRemovalService' && second === 'dashboard') {
    return { locale, page: 'background-removal-dashboard' };
  }

  if (segments.length === 1) {
    if (firstKey === 'contact') return { locale, page: 'contact' };
    if (firstKey === 'about') return { locale, page: 'about' };
    if (firstKey === 'editorialPolicy') return { locale, page: 'editorial-policy' };
    if (firstKey === 'privacyPolicy') return { locale, page: 'privacy-policy' };
    if (firstKey === 'termsOfService') return { locale, page: 'terms-of-service' };
    if (firstKey === 'iosApp') return { locale, page: 'ios-app' };
    if (firstKey === 'passportPhotoMaker') return { locale, page: 'passport-photo-maker' };
    if (firstKey === 'passportPhotoChecker') return { locale, page: 'passport-photo-checker' };
    if (firstKey === 'digitalPassportPhoto') return { locale, page: 'digital-passport-photo' };
    if (firstKey === 'twoByTwoPhotoMaker') return { locale, page: '2x2-passport-photo-maker' };
    if (firstKey === 'usPassportPhotoMaker') return { locale, page: 'us-passport-photo-maker' };
    if (firstKey === 'passport600x600Maker') return { locale, page: '600x600-passport-photo-maker' };
    if (firstKey === 'usVisaPhotoMaker') return { locale, page: 'us-visa-photo-maker' };
    if (firstKey === 'passportPhotoCropTool') return { locale, page: 'passport-photo-crop-tool' };
    if (firstKey === 'passportPhotoResizer') return { locale, page: 'passport-photo-resizer' };
    if (firstKey === 'passportRequirements') return { locale, page: 'passport-requirements' };
    if (firstKey === 'visaRequirements') return { locale, page: 'visa-requirements' };
    if (firstKey === 'idRequirements') return { locale, page: 'id-requirements' };
    if (firstKey === 'drivingLicenceRequirements') return { locale, page: 'driving-licence-requirements' };
    if (firstKey === 'biometricRequirements') return { locale, page: 'biometric-requirements' };
    if (firstKey === 'studentIdRequirements') return { locale, page: 'student-id-requirements' };
    if (firstKey === 'workPermitRequirements') return { locale, page: 'work-permit-requirements' };
    if (firstKey === 'noSignup') return { locale, page: 'no-signup' };
    if (firstKey === 'noWatermark') return { locale, page: 'no-watermark' };
    if (firstKey === 'withoutUploading') return { locale, page: 'without-uploading' };
    if (firstKey === 'sheetTemplate') return { locale, page: 'sheet-template' };
    if (firstKey === 'driversLicenseMaker') return { locale, page: 'drivers-license-maker' };
    if (firstKey === 'greenCard2x2') return { locale, page: 'green-card-2x2' };
    if (firstKey === 'ukPassport600x750') return { locale, page: 'uk-passport-600x750' };
    if (firstKey === 'mmPixelsCalculator') return { locale, page: 'mm-pixels-calculator' };
    if (firstKey === 'dpiPixels') return { locale, page: 'dpi-pixels' };
    if (firstKey === 'printLayouts') return { locale, page: 'print-layouts' };
    if (firstKey === 'idImposer') return { locale, page: 'id-imposer' };
    if (firstKey === 'backgroundRemoval') return { locale, page: 'background-removal' };
    if (firstKey === 'backgroundRemovalService') return { locale, page: 'background-removal-service' };
    if (firstKey === 'sizeComparison') return { locale, page: 'size-comparison' };
    if (firstKey === 'headSizeRules') return { locale, page: 'head-size-rules' };
    if (firstKey === 'blog') return { locale, page: 'blog-list' };
    if (firstKey === 'guide') return { locale, page: 'guide' };
    if (firstKey === 'app') return { locale, page: 'app' };
    if (firstKey && LANDING_SECTION_KEYS.includes(firstKey as LandingSectionKey)) {
      return { locale, page: 'landing', section: firstKey as LandingSectionKey };
    }
  }

  if (segments.length === 2 && firstKey === 'blog') {
    const baseMeta = resolvePostRouteEntryByLocalizedSlug(locale, second);
    const legacyMeta = baseMeta ? undefined : resolveLegacyPostRouteEntryByLocalizedSlug(locale, second);
    return { locale, page: 'blog-post', baseSlug: baseMeta?.slug ?? legacyMeta?.slug, routeSlug: second };
  }

  return { locale, page: 'app' };
}

function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${LOCALE_PREFIXES[locale]}`;
}

export function buildPagePath(locale: Locale, page: SitePageKey): string {
  const prefix = localePrefix(locale);
  if (page === 'home') return prefix || '/';
  return `${prefix}/${PAGE_SEGMENTS[locale][page]}`;
}

export function buildLandingPath(locale: Locale, section?: LandingSectionKey): string {
  if (!section) return buildPagePath(locale, 'home');
  return buildPagePath(locale, section);
}

export function buildSupportPath(locale: Locale): string {
  return `${buildPagePath(locale, 'home')}#support`;
}

export function buildBackgroundRemovalContactPath(locale: Locale): string {
  return `${buildPagePath(locale, 'backgroundRemovalService')}/contact`;
}

export function buildBackgroundRemovalPricingPath(locale: Locale): string {
  return `${buildPagePath(locale, 'backgroundRemovalService')}/pricing`;
}

export function buildBackgroundRemovalDashboardPath(locale: Locale): string {
  return `${buildPagePath(locale, 'backgroundRemovalService')}/dashboard`;
}

export function buildImageToolsPath(locale: Locale = DEFAULT_LOCALE): string {
  const prefix = localePrefix(locale);
  return `${prefix}/${getImageToolsRouteSegment(locale)}`;
}

export function buildImageToolPath(toolIdOrSlug: string, locale: Locale = DEFAULT_LOCALE): string {
  const tool = getImageToolBySlug(toolIdOrSlug);
  const localizedSlug = tool ? getImageToolLocalizedSlug(locale, tool.id) : toolIdOrSlug;
  return `${buildImageToolsPath(locale)}/${localizedSlug}`;
}

export function buildBlogPostPath(locale: Locale, baseSlug: string): string {
  const blogPath = buildPagePath(locale, 'blog');
  return `${blogPath}/${getLocalizedPostRouteSlug(locale, baseSlug)}`;
}

export function canonicalizePathname(pathname: string): string {
  const route = matchPathname(pathname);
  switch (route.page) {
    case 'landing':
      return buildLandingPath(route.locale, route.section);
    case 'contact':
      return buildPagePath(route.locale, 'contact');
    case 'about':
      return buildPagePath(route.locale, 'about');
    case 'editorial-policy':
      return buildPagePath(route.locale, 'editorialPolicy');
    case 'privacy-policy':
      return buildPagePath(route.locale, 'privacyPolicy');
    case 'terms-of-service':
      return buildPagePath(route.locale, 'termsOfService');
    case 'ios-app':
      return buildPagePath(route.locale, 'iosApp');
    case 'passport-photo-maker':
      return buildPagePath(route.locale, 'passportPhotoMaker');
    case 'passport-photo-checker':
      return buildPagePath(route.locale, 'passportPhotoChecker');
    case 'digital-passport-photo':
      return buildPagePath(route.locale, 'digitalPassportPhoto');
    case '2x2-passport-photo-maker':
      return buildPagePath(route.locale, 'twoByTwoPhotoMaker');
    case 'us-passport-photo-maker':
      return buildPagePath(route.locale, 'usPassportPhotoMaker');
    case '600x600-passport-photo-maker':
      return buildPagePath(route.locale, 'passport600x600Maker');
    case 'us-visa-photo-maker':
      return buildPagePath(route.locale, 'usVisaPhotoMaker');
    case 'passport-photo-crop-tool':
      return buildPagePath(route.locale, 'passportPhotoCropTool');
    case 'passport-photo-resizer':
      return buildPagePath(route.locale, 'passportPhotoResizer');
    case 'passport-requirements':
      return buildPagePath(route.locale, 'passportRequirements');
    case 'visa-requirements':
      return buildPagePath(route.locale, 'visaRequirements');
    case 'id-requirements':
      return buildPagePath(route.locale, 'idRequirements');
    case 'driving-licence-requirements':
      return buildPagePath(route.locale, 'drivingLicenceRequirements');
    case 'biometric-requirements':
      return buildPagePath(route.locale, 'biometricRequirements');
    case 'student-id-requirements':
      return buildPagePath(route.locale, 'studentIdRequirements');
    case 'work-permit-requirements':
      return buildPagePath(route.locale, 'workPermitRequirements');
    case 'no-signup':
      return buildPagePath(route.locale, 'noSignup');
    case 'no-watermark':
      return buildPagePath(route.locale, 'noWatermark');
    case 'without-uploading':
      return buildPagePath(route.locale, 'withoutUploading');
    case 'sheet-template':
      return buildPagePath(route.locale, 'sheetTemplate');
    case 'drivers-license-maker':
      return buildPagePath(route.locale, 'driversLicenseMaker');
    case 'green-card-2x2':
      return buildPagePath(route.locale, 'greenCard2x2');
    case 'uk-passport-600x750':
      return buildPagePath(route.locale, 'ukPassport600x750');
    case 'mm-pixels-calculator':
      return buildPagePath(route.locale, 'mmPixelsCalculator');
    case 'dpi-pixels':
      return buildPagePath(route.locale, 'dpiPixels');
    case 'print-layouts':
      return buildPagePath(route.locale, 'printLayouts');
    case 'id-imposer':
      return buildPagePath(route.locale, 'idImposer');
    case 'background-removal':
      return buildPagePath(route.locale, 'backgroundRemoval');
    case 'background-removal-service':
      return buildPagePath(route.locale, 'backgroundRemovalService');
    case 'background-removal-pricing':
      return buildBackgroundRemovalPricingPath(route.locale);
    case 'background-removal-contact':
      return buildBackgroundRemovalContactPath(route.locale);
    case 'background-removal-dashboard':
      return buildBackgroundRemovalDashboardPath(route.locale);
    case 'image-tools':
      return buildImageToolsPath(route.locale);
    case 'image-tool':
      return buildImageToolPath(route.toolSlug, route.locale);
    case 'size-comparison':
      return buildPagePath(route.locale, 'sizeComparison');
    case 'head-size-rules':
      return buildPagePath(route.locale, 'headSizeRules');
    case 'blog-list':
      return buildPagePath(route.locale, 'blog');
    case 'blog-post':
      return route.baseSlug ? buildBlogPostPath(route.locale, route.baseSlug) : buildPagePath(route.locale, 'blog');
    case 'guide':
      return buildPagePath(route.locale, 'guide');
    case 'app':
      return buildPagePath(route.locale, 'app');
  }
}

export function translatePathname(pathname: string, targetLocale: Locale): string {
  const route = matchPathname(pathname);
  switch (route.page) {
    case 'landing':
      return buildLandingPath(targetLocale, route.section);
    case 'contact':
      return buildPagePath(targetLocale, 'contact');
    case 'about':
      return buildPagePath(targetLocale, 'about');
    case 'editorial-policy':
      return buildPagePath(targetLocale, 'editorialPolicy');
    case 'privacy-policy':
      return buildPagePath(targetLocale, 'privacyPolicy');
    case 'terms-of-service':
      return buildPagePath(targetLocale, 'termsOfService');
    case 'ios-app':
      return buildPagePath(targetLocale, 'iosApp');
    case 'passport-photo-maker':
      return buildPagePath(targetLocale, 'passportPhotoMaker');
    case 'passport-photo-checker':
      return buildPagePath(targetLocale, 'passportPhotoChecker');
    case 'digital-passport-photo':
      return buildPagePath(targetLocale, 'digitalPassportPhoto');
    case '2x2-passport-photo-maker':
      return buildPagePath(targetLocale, 'twoByTwoPhotoMaker');
    case 'us-passport-photo-maker':
      return buildPagePath(targetLocale, 'usPassportPhotoMaker');
    case '600x600-passport-photo-maker':
      return buildPagePath(targetLocale, 'passport600x600Maker');
    case 'us-visa-photo-maker':
      return buildPagePath(targetLocale, 'usVisaPhotoMaker');
    case 'passport-photo-crop-tool':
      return buildPagePath(targetLocale, 'passportPhotoCropTool');
    case 'passport-photo-resizer':
      return buildPagePath(targetLocale, 'passportPhotoResizer');
    case 'passport-requirements':
      return buildPagePath(targetLocale, 'passportRequirements');
    case 'visa-requirements':
      return buildPagePath(targetLocale, 'visaRequirements');
    case 'id-requirements':
      return buildPagePath(targetLocale, 'idRequirements');
    case 'driving-licence-requirements':
      return buildPagePath(targetLocale, 'drivingLicenceRequirements');
    case 'biometric-requirements':
      return buildPagePath(targetLocale, 'biometricRequirements');
    case 'student-id-requirements':
      return buildPagePath(targetLocale, 'studentIdRequirements');
    case 'work-permit-requirements':
      return buildPagePath(targetLocale, 'workPermitRequirements');
    case 'no-signup':
      return buildPagePath(targetLocale, 'noSignup');
    case 'no-watermark':
      return buildPagePath(targetLocale, 'noWatermark');
    case 'without-uploading':
      return buildPagePath(targetLocale, 'withoutUploading');
    case 'sheet-template':
      return buildPagePath(targetLocale, 'sheetTemplate');
    case 'drivers-license-maker':
      return buildPagePath(targetLocale, 'driversLicenseMaker');
    case 'green-card-2x2':
      return buildPagePath(targetLocale, 'greenCard2x2');
    case 'uk-passport-600x750':
      return buildPagePath(targetLocale, 'ukPassport600x750');
    case 'mm-pixels-calculator':
      return buildPagePath(targetLocale, 'mmPixelsCalculator');
    case 'dpi-pixels':
      return buildPagePath(targetLocale, 'dpiPixels');
    case 'print-layouts':
      return buildPagePath(targetLocale, 'printLayouts');
    case 'id-imposer':
      return buildPagePath(targetLocale, 'idImposer');
    case 'background-removal':
      return buildPagePath(targetLocale, 'backgroundRemoval');
    case 'background-removal-service':
      return buildPagePath(targetLocale, 'backgroundRemovalService');
    case 'background-removal-pricing':
      return buildBackgroundRemovalPricingPath(targetLocale);
    case 'background-removal-contact':
      return buildBackgroundRemovalContactPath(targetLocale);
    case 'background-removal-dashboard':
      return buildBackgroundRemovalDashboardPath(targetLocale);
    case 'image-tools':
      return buildImageToolsPath(targetLocale);
    case 'image-tool':
      return buildImageToolPath(route.toolSlug, targetLocale);
    case 'size-comparison':
      return buildPagePath(targetLocale, 'sizeComparison');
    case 'head-size-rules':
      return buildPagePath(targetLocale, 'headSizeRules');
    case 'blog-list':
      return buildPagePath(targetLocale, 'blog');
    case 'blog-post':
      return route.baseSlug
        ? buildBlogPostPath(targetLocale, route.baseSlug)
        : buildPagePath(targetLocale, 'blog');
    case 'guide':
      return buildPagePath(targetLocale, 'guide');
    case 'app':
      return buildPagePath(targetLocale, 'app');
  }
}

export function buildLocalizedUrlMap(route: RouteMatch): Array<{ locale: Locale; pathname: string }> {
  if (route.page === 'blog-post' && !route.baseSlug) return [{ locale: route.locale, pathname: canonicalizePathname(buildPagePath(route.locale, 'blog')) }];

  const locales =
    route.page === 'blog-post' && route.baseSlug
      ? getPostRouteEntry(route.baseSlug)
        ? SUPPORTED_LOCALES
        : []
      : SUPPORTED_LOCALES;

  return locales.map((locale) => {
    switch (route.page) {
      case 'landing':
        return { locale, pathname: buildLandingPath(locale, route.section) };
      case 'image-tools':
        return { locale, pathname: buildImageToolsPath(locale) };
      case 'image-tool':
        return { locale, pathname: buildImageToolPath(route.toolSlug, locale) };
      case 'contact':
        return { locale, pathname: buildPagePath(locale, 'contact') };
      case 'about':
        return { locale, pathname: buildPagePath(locale, 'about') };
      case 'editorial-policy':
        return { locale, pathname: buildPagePath(locale, 'editorialPolicy') };
      case 'privacy-policy':
        return { locale, pathname: buildPagePath(locale, 'privacyPolicy') };
      case 'terms-of-service':
        return { locale, pathname: buildPagePath(locale, 'termsOfService') };
      case 'ios-app':
        return { locale, pathname: buildPagePath(locale, 'iosApp') };
      case 'passport-photo-maker':
        return { locale, pathname: buildPagePath(locale, 'passportPhotoMaker') };
      case 'passport-photo-checker':
        return { locale, pathname: buildPagePath(locale, 'passportPhotoChecker') };
      case 'digital-passport-photo':
        return { locale, pathname: buildPagePath(locale, 'digitalPassportPhoto') };
      case '2x2-passport-photo-maker':
        return { locale, pathname: buildPagePath(locale, 'twoByTwoPhotoMaker') };
      case 'us-passport-photo-maker':
        return { locale, pathname: buildPagePath(locale, 'usPassportPhotoMaker') };
      case '600x600-passport-photo-maker':
        return { locale, pathname: buildPagePath(locale, 'passport600x600Maker') };
      case 'us-visa-photo-maker':
        return { locale, pathname: buildPagePath(locale, 'usVisaPhotoMaker') };
      case 'passport-photo-crop-tool':
        return { locale, pathname: buildPagePath(locale, 'passportPhotoCropTool') };
      case 'passport-photo-resizer':
        return { locale, pathname: buildPagePath(locale, 'passportPhotoResizer') };
      case 'passport-requirements':
        return { locale, pathname: buildPagePath(locale, 'passportRequirements') };
      case 'visa-requirements':
        return { locale, pathname: buildPagePath(locale, 'visaRequirements') };
      case 'id-requirements':
        return { locale, pathname: buildPagePath(locale, 'idRequirements') };
      case 'driving-licence-requirements':
        return { locale, pathname: buildPagePath(locale, 'drivingLicenceRequirements') };
      case 'biometric-requirements':
        return { locale, pathname: buildPagePath(locale, 'biometricRequirements') };
      case 'student-id-requirements':
        return { locale, pathname: buildPagePath(locale, 'studentIdRequirements') };
      case 'work-permit-requirements':
        return { locale, pathname: buildPagePath(locale, 'workPermitRequirements') };
      case 'no-signup':
        return { locale, pathname: buildPagePath(locale, 'noSignup') };
      case 'no-watermark':
        return { locale, pathname: buildPagePath(locale, 'noWatermark') };
      case 'without-uploading':
        return { locale, pathname: buildPagePath(locale, 'withoutUploading') };
      case 'sheet-template':
        return { locale, pathname: buildPagePath(locale, 'sheetTemplate') };
      case 'drivers-license-maker':
        return { locale, pathname: buildPagePath(locale, 'driversLicenseMaker') };
      case 'green-card-2x2':
        return { locale, pathname: buildPagePath(locale, 'greenCard2x2') };
      case 'uk-passport-600x750':
        return { locale, pathname: buildPagePath(locale, 'ukPassport600x750') };
      case 'mm-pixels-calculator':
        return { locale, pathname: buildPagePath(locale, 'mmPixelsCalculator') };
      case 'dpi-pixels':
        return { locale, pathname: buildPagePath(locale, 'dpiPixels') };
      case 'print-layouts':
        return { locale, pathname: buildPagePath(locale, 'printLayouts') };
      case 'id-imposer':
        return { locale, pathname: buildPagePath(locale, 'idImposer') };
      case 'background-removal':
        return { locale, pathname: buildPagePath(locale, 'backgroundRemoval') };
      case 'background-removal-service':
        return { locale, pathname: buildPagePath(locale, 'backgroundRemovalService') };
      case 'background-removal-pricing':
        return { locale, pathname: buildBackgroundRemovalPricingPath(locale) };
      case 'background-removal-contact':
        return { locale, pathname: buildBackgroundRemovalContactPath(locale) };
      case 'background-removal-dashboard':
        return { locale, pathname: buildBackgroundRemovalDashboardPath(locale) };
      case 'size-comparison':
        return { locale, pathname: buildPagePath(locale, 'sizeComparison') };
      case 'head-size-rules':
        return { locale, pathname: buildPagePath(locale, 'headSizeRules') };
      case 'blog-list':
        return { locale, pathname: buildPagePath(locale, 'blog') };
      case 'blog-post':
        return { locale, pathname: buildBlogPostPath(locale, route.baseSlug!) };
      case 'guide':
        return { locale, pathname: buildPagePath(locale, 'guide') };
      case 'app':
        return { locale, pathname: buildPagePath(locale, 'app') };
    }
  });
}

export function getPrerenderPathnames(): string[] {
  const paths = new Set<string>();

  for (const locale of SUPPORTED_LOCALES) {
    paths.add(buildLandingPath(locale));
    for (const section of LANDING_SECTION_KEYS) {
      paths.add(buildLandingPath(locale, section));
    }
    paths.add(buildPagePath(locale, 'contact'));
    paths.add(buildPagePath(locale, 'about'));
    paths.add(buildPagePath(locale, 'editorialPolicy'));
    paths.add(buildPagePath(locale, 'privacyPolicy'));
    paths.add(buildPagePath(locale, 'termsOfService'));
    paths.add(buildPagePath(locale, 'iosApp'));
    paths.add(buildPagePath(locale, 'passportPhotoMaker'));
    paths.add(buildPagePath(locale, 'passportPhotoChecker'));
    paths.add(buildPagePath(locale, 'digitalPassportPhoto'));
    paths.add(buildPagePath(locale, 'twoByTwoPhotoMaker'));
    paths.add(buildPagePath(locale, 'usPassportPhotoMaker'));
    paths.add(buildPagePath(locale, 'passport600x600Maker'));
    paths.add(buildPagePath(locale, 'usVisaPhotoMaker'));
    paths.add(buildPagePath(locale, 'passportPhotoCropTool'));
    paths.add(buildPagePath(locale, 'passportPhotoResizer'));
    paths.add(buildPagePath(locale, 'passportRequirements'));
    paths.add(buildPagePath(locale, 'visaRequirements'));
    paths.add(buildPagePath(locale, 'idRequirements'));
    paths.add(buildPagePath(locale, 'drivingLicenceRequirements'));
    paths.add(buildPagePath(locale, 'biometricRequirements'));
    paths.add(buildPagePath(locale, 'studentIdRequirements'));
    paths.add(buildPagePath(locale, 'workPermitRequirements'));
    paths.add(buildPagePath(locale, 'noSignup'));
    paths.add(buildPagePath(locale, 'noWatermark'));
    paths.add(buildPagePath(locale, 'withoutUploading'));
    paths.add(buildPagePath(locale, 'sheetTemplate'));
    paths.add(buildPagePath(locale, 'driversLicenseMaker'));
    paths.add(buildPagePath(locale, 'greenCard2x2'));
    paths.add(buildPagePath(locale, 'ukPassport600x750'));
    paths.add(buildPagePath(locale, 'mmPixelsCalculator'));
    paths.add(buildPagePath(locale, 'dpiPixels'));
    paths.add(buildPagePath(locale, 'printLayouts'));
    paths.add(buildPagePath(locale, 'idImposer'));
    paths.add(buildPagePath(locale, 'backgroundRemoval'));
    paths.add(buildPagePath(locale, 'backgroundRemovalService'));
    paths.add(buildBackgroundRemovalPricingPath(locale));
    paths.add(buildBackgroundRemovalDashboardPath(locale));
    paths.add(buildBackgroundRemovalContactPath(locale));
    paths.add(buildPagePath(locale, 'sizeComparison'));
    paths.add(buildPagePath(locale, 'headSizeRules'));
    paths.add(buildPagePath(locale, 'blog'));
    paths.add(buildPagePath(locale, 'guide'));
    paths.add(buildPagePath(locale, 'app'));
    for (const post of POST_ROUTE_INDEX) {
      if (!post.indexable) continue;
      paths.add(buildBlogPostPath(locale, post.slug));
    }
    paths.add(buildImageToolsPath(locale));
    for (const tool of IMAGE_TOOLS) {
      paths.add(buildImageToolPath(tool.id, locale));
    }
  }

  return [...paths];
}

export function getIndexablePathnames(): string[] {
  const paths = new Set<string>();

  for (const locale of SUPPORTED_LOCALES) {
    paths.add(buildLandingPath(locale));
    for (const section of LANDING_SECTION_KEYS) {
      paths.add(buildLandingPath(locale, section));
    }
    paths.add(buildPagePath(locale, 'contact'));
    paths.add(buildPagePath(locale, 'about'));
    paths.add(buildPagePath(locale, 'editorialPolicy'));
    paths.add(buildPagePath(locale, 'privacyPolicy'));
    paths.add(buildPagePath(locale, 'termsOfService'));
    paths.add(buildPagePath(locale, 'iosApp'));
    paths.add(buildPagePath(locale, 'passportPhotoMaker'));
    paths.add(buildPagePath(locale, 'passportPhotoChecker'));
    paths.add(buildPagePath(locale, 'digitalPassportPhoto'));
    paths.add(buildPagePath(locale, 'twoByTwoPhotoMaker'));
    paths.add(buildPagePath(locale, 'usPassportPhotoMaker'));
    paths.add(buildPagePath(locale, 'passport600x600Maker'));
    paths.add(buildPagePath(locale, 'usVisaPhotoMaker'));
    paths.add(buildPagePath(locale, 'passportPhotoCropTool'));
    paths.add(buildPagePath(locale, 'passportPhotoResizer'));
    paths.add(buildPagePath(locale, 'passportRequirements'));
    paths.add(buildPagePath(locale, 'visaRequirements'));
    paths.add(buildPagePath(locale, 'idRequirements'));
    paths.add(buildPagePath(locale, 'drivingLicenceRequirements'));
    paths.add(buildPagePath(locale, 'biometricRequirements'));
    paths.add(buildPagePath(locale, 'studentIdRequirements'));
    paths.add(buildPagePath(locale, 'workPermitRequirements'));
    paths.add(buildPagePath(locale, 'noSignup'));
    paths.add(buildPagePath(locale, 'noWatermark'));
    paths.add(buildPagePath(locale, 'withoutUploading'));
    paths.add(buildPagePath(locale, 'sheetTemplate'));
    paths.add(buildPagePath(locale, 'driversLicenseMaker'));
    paths.add(buildPagePath(locale, 'greenCard2x2'));
    paths.add(buildPagePath(locale, 'ukPassport600x750'));
    paths.add(buildPagePath(locale, 'mmPixelsCalculator'));
    paths.add(buildPagePath(locale, 'dpiPixels'));
    paths.add(buildPagePath(locale, 'printLayouts'));
    paths.add(buildPagePath(locale, 'idImposer'));
    paths.add(buildPagePath(locale, 'backgroundRemoval'));
    paths.add(buildPagePath(locale, 'backgroundRemovalService'));
    paths.add(buildBackgroundRemovalPricingPath(locale));
    paths.add(buildBackgroundRemovalContactPath(locale));
    paths.add(buildPagePath(locale, 'sizeComparison'));
    paths.add(buildPagePath(locale, 'headSizeRules'));
    paths.add(buildPagePath(locale, 'blog'));
    paths.add(buildPagePath(locale, 'guide'));
    paths.add(buildPagePath(locale, 'app'));
    for (const post of POST_ROUTE_INDEX) {
      paths.add(buildBlogPostPath(locale, post.slug));
    }
    paths.add(buildImageToolsPath(locale));
    for (const tool of IMAGE_TOOLS) {
      paths.add(buildImageToolPath(tool.id, locale));
    }
  }

  return [...paths];
}

export function getIndexableSitemapEntries(): Array<{ pathname: string; lastmod: string }> {
  const entries = new Map<string, string>();

  for (const locale of SUPPORTED_LOCALES) {
    entries.set(buildLandingPath(locale), STATIC_ROUTE_LASTMOD.home);
    for (const section of LANDING_SECTION_KEYS) {
      entries.set(buildLandingPath(locale, section), STATIC_ROUTE_LASTMOD[section]);
    }
    entries.set(buildPagePath(locale, 'contact'), STATIC_ROUTE_LASTMOD.contact);
    entries.set(buildPagePath(locale, 'about'), STATIC_ROUTE_LASTMOD.about);
    entries.set(buildPagePath(locale, 'editorialPolicy'), STATIC_ROUTE_LASTMOD.editorialPolicy);
    entries.set(buildPagePath(locale, 'privacyPolicy'), STATIC_ROUTE_LASTMOD.privacyPolicy);
    entries.set(buildPagePath(locale, 'termsOfService'), STATIC_ROUTE_LASTMOD.termsOfService);
    entries.set(buildPagePath(locale, 'iosApp'), STATIC_ROUTE_LASTMOD.iosApp);
    entries.set(buildPagePath(locale, 'passportPhotoMaker'), STATIC_ROUTE_LASTMOD.passportPhotoMaker);
    entries.set(buildPagePath(locale, 'passportPhotoChecker'), STATIC_ROUTE_LASTMOD.passportPhotoChecker);
    entries.set(buildPagePath(locale, 'digitalPassportPhoto'), STATIC_ROUTE_LASTMOD.digitalPassportPhoto);
    entries.set(buildPagePath(locale, 'twoByTwoPhotoMaker'), STATIC_ROUTE_LASTMOD.twoByTwoPhotoMaker);
    entries.set(buildPagePath(locale, 'usPassportPhotoMaker'), STATIC_ROUTE_LASTMOD.usPassportPhotoMaker);
    entries.set(buildPagePath(locale, 'passport600x600Maker'), STATIC_ROUTE_LASTMOD.passport600x600Maker);
    entries.set(buildPagePath(locale, 'usVisaPhotoMaker'), STATIC_ROUTE_LASTMOD.usVisaPhotoMaker);
    entries.set(buildPagePath(locale, 'passportPhotoCropTool'), STATIC_ROUTE_LASTMOD.passportPhotoCropTool);
    entries.set(buildPagePath(locale, 'passportPhotoResizer'), STATIC_ROUTE_LASTMOD.passportPhotoResizer);
    entries.set(buildPagePath(locale, 'passportRequirements'), STATIC_ROUTE_LASTMOD.passportRequirements);
    entries.set(buildPagePath(locale, 'visaRequirements'), STATIC_ROUTE_LASTMOD.visaRequirements);
    entries.set(buildPagePath(locale, 'idRequirements'), STATIC_ROUTE_LASTMOD.idRequirements);
    entries.set(buildPagePath(locale, 'drivingLicenceRequirements'), STATIC_ROUTE_LASTMOD.drivingLicenceRequirements);
    entries.set(buildPagePath(locale, 'biometricRequirements'), STATIC_ROUTE_LASTMOD.biometricRequirements);
    entries.set(buildPagePath(locale, 'studentIdRequirements'), STATIC_ROUTE_LASTMOD.studentIdRequirements);
    entries.set(buildPagePath(locale, 'workPermitRequirements'), STATIC_ROUTE_LASTMOD.workPermitRequirements);
    entries.set(buildPagePath(locale, 'noSignup'), STATIC_ROUTE_LASTMOD.noSignup);
    entries.set(buildPagePath(locale, 'noWatermark'), STATIC_ROUTE_LASTMOD.noWatermark);
    entries.set(buildPagePath(locale, 'withoutUploading'), STATIC_ROUTE_LASTMOD.withoutUploading);
    entries.set(buildPagePath(locale, 'sheetTemplate'), STATIC_ROUTE_LASTMOD.sheetTemplate);
    entries.set(buildPagePath(locale, 'driversLicenseMaker'), STATIC_ROUTE_LASTMOD.driversLicenseMaker);
    entries.set(buildPagePath(locale, 'greenCard2x2'), STATIC_ROUTE_LASTMOD.greenCard2x2);
    entries.set(buildPagePath(locale, 'ukPassport600x750'), STATIC_ROUTE_LASTMOD.ukPassport600x750);
    entries.set(buildPagePath(locale, 'mmPixelsCalculator'), STATIC_ROUTE_LASTMOD.mmPixelsCalculator);
    entries.set(buildPagePath(locale, 'dpiPixels'), STATIC_ROUTE_LASTMOD.dpiPixels);
    entries.set(buildPagePath(locale, 'printLayouts'), STATIC_ROUTE_LASTMOD.printLayouts);
    entries.set(buildPagePath(locale, 'idImposer'), STATIC_ROUTE_LASTMOD.idImposer);
    entries.set(buildPagePath(locale, 'backgroundRemoval'), STATIC_ROUTE_LASTMOD.backgroundRemoval);
    entries.set(buildPagePath(locale, 'backgroundRemovalService'), STATIC_ROUTE_LASTMOD.backgroundRemovalService);
    entries.set(buildBackgroundRemovalPricingPath(locale), BACKGROUND_REMOVAL_PRICING_LASTMOD);
    entries.set(buildBackgroundRemovalContactPath(locale), BACKGROUND_REMOVAL_CONTACT_LASTMOD);
    entries.set(buildPagePath(locale, 'sizeComparison'), STATIC_ROUTE_LASTMOD.sizeComparison);
    entries.set(buildPagePath(locale, 'headSizeRules'), STATIC_ROUTE_LASTMOD.headSizeRules);
    entries.set(buildPagePath(locale, 'blog'), STATIC_ROUTE_LASTMOD.blog);
    entries.set(buildPagePath(locale, 'guide'), STATIC_ROUTE_LASTMOD.guide);
    entries.set(buildPagePath(locale, 'app'), APP_ROUTE_LASTMOD);
    for (const post of POST_ROUTE_INDEX) {
      if (!post.indexable) continue;
      entries.set(buildBlogPostPath(locale, post.slug), post.updatedAt);
    }
    entries.set(buildImageToolsPath(locale), IMAGE_TOOLS_LASTMOD);
    for (const tool of IMAGE_TOOLS) {
      entries.set(buildImageToolPath(tool.id, locale), IMAGE_TOOLS_LASTMOD);
    }
  }

  for (const entry of STANDALONE_SITEMAP_ENTRIES) {
    entries.set(entry.pathname, entry.lastmod);
  }

  return [...entries.entries()].map(([pathname, lastmod]) => ({ pathname, lastmod }));
}
